"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createSsoRouter = void 0;
const injection_symbols_1 = require("./injection-symbols");
/**
 * Creates the SSO Router composable instance, for use as a plugin in the Vue app.
 */
function createSsoRouter(options) {
    const { directiveName, ledsService, monitoringService } = options;
    /**
     * Given the SSO script name to run and params to pass to the script, generates a URL
     * that you can redirect the browser to in order to run that SSO.
     * This URL can EXPIRE, so do not generate it and leave it in a link sitting on the page.
     * @param ssoName Name of the SSO script (currently stored in WCM) to run.
     * @param params Params to pass to the SSO script.
     * @returns URL that can be navigated to in order to run the SSO.
     */
    function generateSsoUrl(ssoName, params) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const authorizationHeaders = yield ledsService.createAuthorizationHeaders();
            const clientData = Object.assign({ 'X-Environment': authorizationHeaders['X-Environment'], 'X-Region': authorizationHeaders['X-Region'] }, params);
            const generateTokenResponse = yield ledsService.client.post(`member/singleSignOns/${encodeURIComponent(ssoName)}/token`, {
                clientData
            });
            if (!((_a = generateTokenResponse.data) === null || _a === void 0 ? void 0 : _a.signOnToken)) {
                throw new Error('Generate SSO token response did not include a token.');
            }
            return ((ledsService.client.defaults.baseURL || '').replace(/\/$/g, '')
                + `/member/singleSignOns/${encodeURIComponent(ssoName)}/${generateTokenResponse.data.signOnToken}`);
        });
    }
    /**
     * Given the SSO script name to run and params to pass to the script, attempts to open the SSO
     * in a new tab, or redirects the current tab if that fails.
     * @param ssoName Name of the SSO script (currently stored in WCM) to run.
     * @param params Params to pass to the SSO script.
     * @returns Promise that should resolve when the redirect completes.
     */
    function redirectToSso(ssoName, params, redirectOptions) {
        return new Promise((resolve, reject) => {
            const newTabWindow = (redirectOptions === null || redirectOptions === void 0 ? void 0 : redirectOptions.forceSameWindow) ? null : window.open();
            if (monitoringService) {
                monitoringService.logEvent('sso.redirect', { ssoName });
            }
            const getParams = Object.prototype.toString.call(params) === '[object Function]'
                ? params : () => Promise.resolve(params);
            getParams().then((ssoParams) => {
                generateSsoUrl(ssoName, ssoParams).then((redirectUrl) => {
                    if (newTabWindow) {
                        newTabWindow.location.href = redirectUrl;
                        resolve();
                    }
                    else {
                        window.location.href = redirectUrl;
                        // Do NOT resolve promise when redirecting the current tab;
                        // better experience for loading animations
                    }
                }).catch((error) => {
                    reject(error);
                });
            }).catch((error) => {
                reject(error);
            });
        });
    }
    // WeakMap for v-sso-to directive to keep track of things
    const ssoToDirectiveSettings = new WeakMap();
    function onClickSsoLink(event) {
        const target = (event.currentTarget || event.target);
        if (!target)
            return;
        const settings = ssoToDirectiveSettings.get(target);
        if (settings) {
            event.preventDefault();
            target.setAttribute('aria-busy', 'true');
            target.dispatchEvent(new Event('sso-redirect-start'));
            redirectToSso(settings.ssoName, settings.params).then(() => {
                target.dispatchEvent(new Event('sso-redirect-end'));
                target.setAttribute('aria-busy', 'false');
            }).catch(() => {
                target.dispatchEvent(new Event('sso-redirect-end'));
                target.dispatchEvent(new Event('sso-redirect-error'));
                target.setAttribute('aria-busy', 'false');
            });
            return false;
        }
    }
    /**
     * v-sso-to directive for easy setup of SSO links.
     * E.g.
     * <a v-sso-to="{ ssoName: 'alegeus', params: { foo: 'bar' } }" />
     */
    const SsoToDirective = {
        mounted(el, binding) {
            var _a;
            el.addEventListener('click', onClickSsoLink);
            if (el.tagName === 'A') {
                el.setAttribute('href', 'javascript:void(0)');
            }
            const settings = {
                ssoName: binding.value.ssoName,
                params: (_a = binding.value.params) !== null && _a !== void 0 ? _a : {}
            };
            ssoToDirectiveSettings.set(el, settings);
            // Populate link href with SSO link, just in case a user right clicks on link.
            // This is not guaranteed to work, though, since these links expire.
            if (el.tagName === 'A') {
                generateSsoUrl(settings.ssoName, settings.params).then((url) => {
                    el.setAttribute('href', url);
                });
            }
        },
        updated(el, binding) {
            var _a;
            const settings = {
                ssoName: binding.value.ssoName,
                params: (_a = binding.value.params) !== null && _a !== void 0 ? _a : {}
            };
            ssoToDirectiveSettings.set(el, settings);
            // Populate link href with SSO link, just in case a user right clicks on link.
            // This is not guaranteed to work, though, since these links expire.
            if (el.tagName === 'A') {
                generateSsoUrl(settings.ssoName, settings.params).then((url) => {
                    el.setAttribute('href', url);
                });
            }
        },
        unmounted(el) {
            el.removeEventListener('click', onClickSsoLink);
        }
    };
    // Build the plugin object.
    const ssoRouter = {
        generateSsoUrl,
        redirectToSso,
        install(app) {
            app.directive(directiveName !== null && directiveName !== void 0 ? directiveName : 'sso-to', SsoToDirective);
            app.provide(injection_symbols_1.ssoRouterKey, ssoRouter);
        }
    };
    return ssoRouter;
}
exports.createSsoRouter = createSsoRouter;
//# sourceMappingURL=sso-router.js.map